var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Element } from 'react-scroll';
import RfpReplacePopup from '@/container/Professional/Rfp/One/Popup/RfpReplacePopup';
import { wrapDispatch } from '@/helpers/action';
import { isClientPreStatus } from '@/helpers/auth';
import { propBool } from '@/helpers/custom-prop-types';
import { compose } from '@/helpers/recompose';
import { scrollToWithHeaderHeight } from '@/helpers/ScrollUtils';
import { useTypeSelector } from '@/hooks/state/useTypeSelector';
import { apiGetListClientMyPageSearchCaseStudy, apiGetListClientMyPageSearchPickup, apiGetListClientMyPageSearchTheme, } from '../../../../actions/ClientMyPageActions';
import Layout from '../../../Layout';
import MediaQueryForConsultantSearchHoc from '../../../Layout/MediaQuery/MediaQueryForConsultantSearchHoc';
import AuthenticationHoc from '../../../System/AuthenticationHoc';
import ExaminationAlertAndHeadCardFullModal from '../../../Ui/Modal/ExaminationAlertAndHeadCardFullModal';
import ModalPortal from '../../../Ui/Modal/ModalPortal';
import { FaqSearch } from '../MyPageCommon/Faq/FaqSearch';
import Navigation from '../MyPageCommon/Navigation/Navigation';
import SearchCard from './SearchCard';
import StudyCaseCard from './StudyCaseCard';
/**
 * TODO: TS化 & tailwind化
 */
const MyPageSearch = (props) => {
    const { isLg, isMd, isXl } = props;
    // reducer処理
    const dispatch = useDispatch();
    const profile = useSelector((state) => state.profile || {});
    const themes = useSelector((state) => state.clientMyPage.themes || []);
    const pickups = useSelector((state) => state.clientMyPage.pickups || []);
    const solutionLargeClass = useSelector((state) => state.category.solutionLargeClass || []);
    const { rfp_unread_count: rfpUnreadCount } = useTypeSelector((state) => state.clientRfpCount);
    const [isFullScreenMode, setIsFullScreenMode] = useState(false);
    const [studyCases, setStudyCases] = useState({});
    const [studyCasesLoading, setStudyCasesLoading] = useState({});
    const [themeLoading, setThemeLoading] = useState(false);
    const [pickupLoading, setPickupLoading] = useState(false);
    const [showPreModal, setShowPreModal] = useState(false);
    const unmountRef = useRef(false);
    const isPre = useMemo(() => isClientPreStatus(profile), [profile]);
    const tags = useMemo(() => solutionLargeClass.filter(({ isApplyRanking }) => isApplyRanking), [solutionLargeClass]);
    const [active, setActive] = useState(tags.length > 0 ? tags[0].id : '');
    const classNameFullScreenMode = useMemo(() => (isFullScreenMode ? 'is_full_screen_mode' : ''), [isFullScreenMode]);
    const solutions = useMemo(() => studyCases[active] || [], [active, studyCases]);
    const solutionLoading = useMemo(() => studyCasesLoading[active] !== false, [active, studyCasesLoading]);
    const safeSetState = useCallback((func, callback) => {
        if (!unmountRef.current) {
            return;
        }
        func(callback);
    }, [unmountRef]);
    const loadTheme = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        safeSetState(setThemeLoading, true);
        try {
            yield wrapDispatch(dispatch, apiGetListClientMyPageSearchTheme());
        }
        finally {
            safeSetState(setThemeLoading, false);
        }
    }), [dispatch, safeSetState]);
    const loadPickup = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        safeSetState(setPickupLoading, true);
        try {
            yield wrapDispatch(dispatch, apiGetListClientMyPageSearchPickup());
        }
        finally {
            safeSetState(setPickupLoading, false);
        }
    }), [dispatch, safeSetState]);
    const loadStudyCase = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        let id = active;
        if (e) {
            id = e.currentTarget.value;
        }
        id = typeof id === 'string' ? parseInt(id, 10) : id;
        safeSetState(setActive, id);
        try {
            safeSetState(setStudyCasesLoading, (prevState) => (Object.assign(Object.assign({}, prevState), { [id]: true })));
            const { payload } = yield wrapDispatch(dispatch, apiGetListClientMyPageSearchCaseStudy(id));
            safeSetState(setStudyCases, (prevState) => (Object.assign(Object.assign({}, prevState), { [id]: payload.list })));
        }
        finally {
            safeSetState(setStudyCasesLoading, (prevState) => (Object.assign(Object.assign({}, prevState), { [id]: false })));
        }
    }), [active, dispatch, safeSetState]);
    useEffect(() => {
        unmountRef.current = true;
        loadTheme();
        loadPickup();
        loadStudyCase();
        return () => {
            unmountRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onClickShowPreModal = useCallback(() => {
        safeSetState(setShowPreModal, true);
    }, [safeSetState]);
    const onClickClosePreModal = useCallback(() => {
        safeSetState(setShowPreModal, false);
    }, [safeSetState]);
    const onClickMyPageLink = useCallback((e) => {
        e.preventDefault();
        scrollToWithHeaderHeight('mypage_navigation');
    }, []);
    return (_jsxs(Layout, Object.assign({ mainClassName: `l-main client_mypage no-need-bottom-space ${classNameFullScreenMode}`, sticky: _jsx(RfpReplacePopup, {}) }, { children: [_jsxs("div", Object.assign({ className: 'mypage_container' }, { children: [_jsx(Element, { name: 'mypage_navigation' }), _jsx(Navigation, { activeTab: 'mypage', rfpUnreadCount: rfpUnreadCount }), _jsx("div", Object.assign({ className: 'mypage_card' }, { children: _jsxs("div", Object.assign({ className: 'card_body' }, { children: [_jsx("div", Object.assign({ className: 'mypage_card_search' }, { children: _jsx(SearchCard, { isLg: isLg, isMd: isMd, isPre: isPre, isXl: isXl, pickupList: pickups, pickupLoading: pickupLoading, setIsFullScreenMode: setIsFullScreenMode, showPreModal: onClickShowPreModal, themeList: themes, themeLoading: themeLoading }) })), _jsxs("div", Object.assign({ className: 'card_body_section mypage_card_case_study' }, { children: [_jsx("div", Object.assign({ className: 'card_body_section_header' }, { children: _jsx("h4", Object.assign({ className: 'card_body_section_header_label' }, { children: "1,000\u4E07\u5186\u4EE5\u4E0B\u306E\u4E8B\u4F8B" })) })), _jsx(StudyCaseCard, { active: active, isLg: isLg, isMd: isMd, isPre: isPre, loadStudyCase: loadStudyCase, showPreModal: onClickShowPreModal, solutionLoading: solutionLoading, solutions: solutions, tags: tags })] }))] })) }))] })), _jsx(FaqSearch, { onClickMyPageLink: onClickMyPageLink }), _jsx(ModalPortal, Object.assign({ selector: '#app' }, { children: _jsx(ExaminationAlertAndHeadCardFullModal, { isShow: showPreModal, onClickClose: onClickClosePreModal }) }))] })));
};
MyPageSearch.propTypes = {
    isLg: propBool().isRequired,
    isMd: propBool().isRequired,
    isXl: propBool().isRequired,
};
const enhance = compose(AuthenticationHoc({ isClient: true }), MediaQueryForConsultantSearchHoc);
export default enhance(MyPageSearch);
